"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Api = exports.AuthStrategy = void 0;
const axios_1 = __importDefault(require("axios"));
const axios_retry_1 = __importDefault(require("axios-retry"));
const http_status_1 = require("http-status");
const header_util_1 = require("./header-util");
const auth_credential_util_1 = require("./auth-credential-util");
const reload_util_1 = require("./reload-util");
const auth_session_util_1 = require("./auth-session-util");
const { REACT_APP_APP_VERSION, REACT_APP_BUILD_NUMBER, REACT_APP_APP_NAME, REACT_APP_USERNAME, REACT_APP_AUTHTOKEN } = process.env;
var eMethod;
(function (eMethod) {
    eMethod["POST"] = "post";
    eMethod["GET"] = "get";
    eMethod["PUT"] = "put";
    eMethod["DELETE"] = "delete";
})(eMethod || (eMethod = {}));
var AuthStrategy;
(function (AuthStrategy) {
    AuthStrategy["SESSION"] = "SESSION";
    AuthStrategy["AUTH_TOKEN"] = "AUTH_TOKEN";
    AuthStrategy["APP_SERVICE_CREDENTIALS"] = "APP_SERVICE_CREDENTIALS";
    AuthStrategy["HTTP_AUTHENTICATION"] = "HTTP_AUTHENTICATION";
})(AuthStrategy = exports.AuthStrategy || (exports.AuthStrategy = {}));
let axiosClient;
let api;
if (process.env.REACT_APP_API_PREFIX && !process.env.REACT_APP_API_URL) {
    console.warn(`Use of the REACT_APP_API_PREFIX environment variable is deprecated. Please use REACT_APP_API_URL with a fully-qualified API URL instead.`);
}
const getBaseLwtRequestHeaders = () => (Object.assign(Object.assign(Object.assign({}, (REACT_APP_APP_NAME && { [header_util_1.Header.APP]: REACT_APP_APP_NAME })), (REACT_APP_APP_VERSION && { [header_util_1.Header.VERSION]: REACT_APP_APP_VERSION })), (REACT_APP_BUILD_NUMBER && { [header_util_1.Header.BUILD]: REACT_APP_BUILD_NUMBER })));
const isRetryableStatus = status => [http_status_1.BAD_GATEWAY, http_status_1.GATEWAY_TIMEOUT].includes(status);
const isRetryableUrl = url => !['/logs', '/events'].includes(url);
const shouldRetryRequest = (error) => {
    var _a, _b;
    const retryable = isRetryableStatus((_a = error.response) === null || _a === void 0 ? void 0 : _a.status) && isRetryableUrl((_b = error.response) === null || _b === void 0 ? void 0 : _b.config.url);
    return retryable;
};
const getDefaultRequestHeaders = () => __awaiter(void 0, void 0, void 0, function* () {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return { [header_util_1.Header.TIMEZONE]: timezone };
});
const pickContext = response => { var _a; return (_a = response === null || response === void 0 ? void 0 : response.config) === null || _a === void 0 ? void 0 : _a.context; };
class Api {
    constructor() {
        this.state = {
            options: { throwErrors: false },
            requestMiddlewares: [],
            getContext: undefined,
            getHeaders: undefined,
            appendErrorLogMetadata: undefined,
            onDone: undefined,
            onError: undefined,
            authContext: undefined,
            authStrategy: undefined
        };
        this.getAuthHeaders = () => __awaiter(this, void 0, void 0, function* () {
            switch (this.state.authStrategy) {
                case AuthStrategy.SESSION:
                    const session = auth_session_util_1.getSessionId();
                    return Object.assign({}, (session && { [header_util_1.Header.SESSION]: session }));
                case AuthStrategy.AUTH_TOKEN:
                    const { username, authToken } = auth_credential_util_1.getAuthCredentials();
                    return Object.assign(Object.assign({}, (username && { [header_util_1.Header.USERNAME]: username })), (authToken && { [header_util_1.Header.AUTH_TOKEN]: authToken }));
                case AuthStrategy.APP_SERVICE_CREDENTIALS:
                    return {
                        [header_util_1.Header.USERNAME]: REACT_APP_USERNAME,
                        [header_util_1.Header.AUTH_TOKEN]: REACT_APP_AUTHTOKEN
                    };
                case AuthStrategy.HTTP_AUTHENTICATION:
                    const context = this.state.authContext;
                    return Object.assign({}, ((context === null || context === void 0 ? void 0 : context.accessToken) && { Authorization: `Bearer ${context.accessToken}` }));
                default:
                    return null;
            }
        });
        this.onRequestMiddleware = (axiosConfig) => __awaiter(this, void 0, void 0, function* () {
            const headerCalls = [getDefaultRequestHeaders(), this.getAuthHeaders()];
            if (this.state.getHeaders)
                headerCalls.push(this.state.getHeaders());
            const [defaultHeaders, authHeaders, otherHeaders] = yield Promise.all(headerCalls);
            axiosConfig.headers = Object.assign(Object.assign(Object.assign(Object.assign({}, axiosConfig.headers), defaultHeaders), authHeaders), otherHeaders);
            return axiosConfig;
        });
        this.onErrorRequestMiddleware = error => Promise.reject(error);
        this.onSuccessResponseMiddleware = (response) => __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            const { status, data } = response;
            const context = pickContext(response);
            yield ((_b = (_a = this.state).onDone) === null || _b === void 0 ? void 0 : _b.call(_a, { status, data, context }));
            return { status, data };
        });
        this.onErrorResponseMiddleware = (error) => __awaiter(this, void 0, void 0, function* () {
            var _c, _d, _e;
            const { status, data, config } = (_c = error.response) !== null && _c !== void 0 ? _c : {};
            const { method, url, params, data: requestData } = config !== null && config !== void 0 ? config : {};
            if (status === http_status_1.PRECONDITION_FAILED) {
                yield reload_util_1.maybeReloadApp(process.env.REACT_APP_APP_VERSION);
                return;
            }
            if (status !== http_status_1.UNAUTHORIZED && status !== http_status_1.FORBIDDEN && isRetryableUrl(url)) {
                const context = pickContext(error.response);
                this.createTelemetryLog({
                    payload: {
                        type: 'api-error',
                        data: Object.assign(Object.assign(Object.assign({ method,
                            url,
                            status, body: data }, (params && { params })), (requestData && {
                            data: typeof requestData === 'string' ? JSON.parse(requestData) : requestData
                        })), (_e = (_d = this.state).appendErrorLogMetadata) === null || _e === void 0 ? void 0 : _e.call(_d, { error, context }))
                    }
                });
            }
            if (this.state.onError)
                return this.state.onError({ status, data, error });
            return this.state.options.throwErrors
                ? Promise.reject(error)
                : { status, data, error };
        });
        this.options = (options = {}) => {
            this.state.options = options;
            return this;
        };
        this.init = () => {
            axiosClient = axios_1.default.create({
                baseURL: process.env.REACT_APP_API_URL,
                headers: getBaseLwtRequestHeaders()
            });
            axiosClient.interceptors.request.use(this.onRequestMiddleware, this.onErrorRequestMiddleware);
            axiosClient.interceptors.response.use(this.onSuccessResponseMiddleware, this.onErrorResponseMiddleware);
            axios_retry_1.default(axiosClient, { retries: 3, retryCondition: shouldRetryRequest });
            return this;
        };
        this.addHeaders = getHeadersCb => {
            this.state.getHeaders = getHeadersCb;
            return this;
        };
        this.addErrorLogMetadata = appendErrorLogMetadataCb => {
            this.state.appendErrorLogMetadata = appendErrorLogMetadataCb;
            return this;
        };
        this.addRequestMiddleware = cb => {
            this.state.requestMiddlewares.push(cb);
            return this;
        };
        this.addRequestContext = contextCb => {
            this.state.getContext = contextCb;
            return this;
        };
        this.getAuthContext = () => {
            return this.state.authContext;
        };
        this.setAuthContext = (authContext) => {
            this.state.authContext = authContext;
        };
        this.addDefaultAuthStrategy = (authStrategy) => {
            this.state.authStrategy = authStrategy;
            return this;
        };
        this.onDone = onDoneCb => {
            this.state.onDone = onDoneCb;
            return this;
        };
        this.onError = onErrorCb => {
            this.state.onError = onErrorCb;
            return this;
        };
        this.createEvent = ({ url, payload }) => __awaiter(this, void 0, void 0, function* () {
            const timestamp = new Date().toISOString();
            try {
                return this.execute({
                    method: Api.Method.POST,
                    url,
                    options: {
                        data: Object.assign({ timestamp }, payload)
                    },
                    authStrategy: this.state.authStrategy
                });
            }
            catch (err) {
                // do nothing
            }
        });
        this.createTelemetryEvent = ({ url = '/events', payload }) => {
            return this.createEvent({ url, payload });
        };
        this.createTelemetryLog = ({ url = '/logs', payload }) => {
            //LEAVING THIS COMMENTED OUT WHILE ON MAIN-NEXT BECAUSE PRM IS THROWING INFINITE LOOP
            // return this.createEvent({
            //   url,
            //   payload
            // });
        };
        this.resolveOverloadArgs = args => {
            var _a;
            const [arg1, arg2] = args;
            const authStrategy = (typeof arg1 === 'string'
                ? arg1
                : this.state.authStrategy);
            const options = (_a = (typeof arg1 === 'string' ? arg2 : arg1)) !== null && _a !== void 0 ? _a : {};
            if (!authStrategy)
                throw new Error('You must specify an AuthStrategy via either 1.) "addDefaultAuthStrategy" method OR 2.) as 2nd arg in "get" | "post" | "put" | "destroy" methods');
            return { authStrategy, options };
        };
        this.invokeRequestMiddlewares = options => this.state.requestMiddlewares.reduce((acc, cb) => {
            var _a;
            const result = (_a = cb(acc)) !== null && _a !== void 0 ? _a : {};
            return result;
        }, options);
        this.execute = ({ method, url, options = {}, authStrategy }) => __awaiter(this, void 0, void 0, function* () {
            var _f, _g;
            const payload = this.invokeRequestMiddlewares(Object.assign({ url }, options));
            const context = Object.assign(Object.assign({}, (_g = (_f = this.state).getContext) === null || _g === void 0 ? void 0 : _g.call(_f, payload)), { authStrategy });
            return axiosClient(Object.assign(Object.assign({ method }, payload), { context }));
        });
        this.get = (url, ...args) => this.execute(Object.assign({ method: Api.Method.GET, url }, this.resolveOverloadArgs(args)));
        this.post = (url, ...args) => this.execute(Object.assign({ method: Api.Method.POST, url }, this.resolveOverloadArgs(args)));
        this.put = (url, ...args) => this.execute(Object.assign({ method: Api.Method.PUT, url }, this.resolveOverloadArgs(args)));
        this.destroy = (url, ...args) => this.execute(Object.assign({ method: Api.Method.DELETE, url }, this.resolveOverloadArgs(args)));
    }
}
exports.Api = Api;
Api.Method = eMethod;
// create singleton so lib and consuming apps can use instance
exports.default = () => {
    if (api)
        return api;
    api = new Api().init();
    return api;
};
